import { useMemo } from 'react';
import { RankedBars } from '../../charts/RankedBars';
import { BarChart } from '../../charts/BarChart';
import { Card, ChartCard, DataTable, Stat, StatusChip, type Column } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import {
  formatCompactCurrency,
  pct,
  periodLabel,
  relativeDays,
  snakeLabel,
  urgency,
} from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import {
  contractBand,
  type ClientOption,
  type ContractRow,
  type ContractsData,
  type VendorOption,
} from './adminTypes';
import { DueCell, RegisterPanel, formatIsoShort, type RegisterSpec } from './adminUi';

const PARTY_TYPE = ['client', 'vendor', 'landlord', 'partner'] as const;
const CONTRACT_TYPE = [
  'managed_services', 'co_managed', 'project_sow', 'software_subscription', 'carrier', 'lease', 'other',
] as const;
const CONTRACT_STATUS = ['active', 'in_renewal', 'notice_given', 'expired', 'draft'] as const;

/**
 * Contracts and renewals.
 *
 * The register with the most at stake here, and the reason is `NoticeDays`. An
 * end date is a diary entry; a notice deadline is the date after which the
 * decision is made for you. Both are shown on every row, and the deadline is what
 * the alerts fire on.
 *
 * Client value and supplier cost are kept apart throughout. They are both money
 * against a contract, but one is revenue and one is spend, and a single "total
 * contract value" figure covering both would be meaningless.
 */
export function AdminContracts({
  canWrite,
  onChanged,
}: {
  canWrite: boolean;
  onChanged: () => void;
}) {
  const analytics = useResource<ContractsData>('/admin/analytics/contracts');
  const clients = useResource<ClientOption[]>('/admin/pickers/clients');
  const vendors = useResource<VendorOption[]>('/admin/pickers/vendors');
  const employees = useResource<Employee[]>('/employees?status=active');

  const spec = useMemo<RegisterSpec<ContractRow>>(
    () => ({
      resource: 'contracts',
      title: 'The contract book',
      singular: 'Contract',
      emptyTitle: 'No contracts recorded yet',
      emptyBody:
        'Add the agreements this business is party to — client managed-services contracts, software subscriptions, carrier services, the office lease. The notice period is the field worth being accurate about: it is what turns a renewal date into a deadline.',
      searchHint: 'Name, counterparty or owner',
      idOf: (row) => row.ContractId,
      labelOf: (row) => row.ContractName,
      filters: [
        { param: 'status', label: 'Status', options: CONTRACT_STATUS },
        { param: 'partyType', label: 'Party', options: PARTY_TYPE },
        { param: 'contractType', label: 'Type', options: CONTRACT_TYPE },
      ],
      columns: [
        {
          key: 'ContractName',
          label: 'Contract',
          render: (row) => (
            <div>
              <div className="admin-cell-title">{row.ContractName}</div>
              <div className="xs muted">
                {row.ClientName ?? row.VendorName ?? row.CounterpartyName} ·{' '}
                {snakeLabel(row.ContractType)}
              </div>
            </div>
          ),
        },
        {
          key: 'Status',
          label: 'Status',
          render: (row) => (
            <StatusChip band={contractBand(row.Status)}>{snakeLabel(row.Status)}</StatusChip>
          ),
        },
        {
          key: 'EndDate',
          label: 'Ends',
          render: (row) => <DueCell date={row.EndDate} days={row.DaysToEnd} />,
        },
        {
          key: 'NoticeDeadline',
          label: 'Notice by',
          render: (row) => (
            <div>
              <div>{formatIsoShort(row.NoticeDeadline)}</div>
              <div className="xs muted">
                {/* Auto-renew is the state where a missed notice date costs
                    another term, so it is called out on the row rather than
                    hidden in the editor. */}
                {row.AutoRenew ? 'auto-renews' : 'does not auto-renew'} · {row.NoticeDays}d notice
              </div>
            </div>
          ),
        },
        {
          key: 'MonthlyValue',
          label: 'Monthly',
          align: 'right',
          render: (row) => formatCompactCurrency(row.MonthlyValue),
        },
        {
          key: 'AnnualValue',
          label: 'Annual',
          align: 'right',
          render: (row) => formatCompactCurrency(row.AnnualValue),
        },
        { key: 'OwnerName', label: 'Owner', render: (row) => row.OwnerName ?? <span className="sub">—</span> },
      ],
      fields: [
        { name: 'contractName', label: 'Contract name', kind: 'text', wide: true },
        { name: 'partyType', label: 'Party type', kind: 'select', options: PARTY_TYPE },
        { name: 'contractType', label: 'Contract type', kind: 'select', options: CONTRACT_TYPE },
        {
          name: 'counterpartyName',
          label: 'Counterparty',
          kind: 'text',
          wide: true,
          hint: 'Required, and kept even when a client or vendor is linked below — a contract has to name who it is with on a printed page.',
        },
        {
          name: 'clientId',
          label: 'Client',
          kind: 'select',
          optional: true,
          placeholder: 'Not a client',
          hint: 'From the shared directory. Links the contract into client MRR.',
          choices: (clients.data ?? []).map((c) => ({
            value: String(c.ClientId),
            label: c.IsActive ? c.Name : `${c.Name} (inactive)`,
          })),
        },
        {
          name: 'adminVendorId',
          label: 'Vendor',
          kind: 'select',
          optional: true,
          placeholder: 'Not a vendor',
          choices: (vendors.data ?? []).map((v) => ({ value: String(v.AdminVendorId), label: v.Name })),
        },
        { name: 'startDate', label: 'Start date', kind: 'date' },
        { name: 'endDate', label: 'End date', kind: 'date' },
        { name: 'termMonths', label: 'Term (months)', kind: 'int' },
        {
          name: 'noticeDays',
          label: 'Notice period (days)',
          kind: 'int',
          hint: 'Days before the end date by which notice must be served.',
        },
        { name: 'autoRenew', label: 'Renews automatically', kind: 'checkbox' },
        { name: 'status', label: 'Status', kind: 'select', options: CONTRACT_STATUS },
        { name: 'monthlyValue', label: 'Monthly value', kind: 'money' },
        { name: 'annualValue', label: 'Annual value', kind: 'money' },
        { name: 'seats', label: 'Seats', kind: 'int' },
        { name: 'priceIncreasePercent', label: 'Price increase %', kind: 'percent' },
        {
          name: 'ownerEmployeeId',
          label: 'Owner',
          kind: 'select',
          optional: true,
          placeholder: 'Unassigned',
          choices: (employees.data ?? []).map((e) => ({
            value: String(e.EmployeeId),
            label: e.FullName,
          })),
        },
        { name: 'lastReviewed', label: 'Last reviewed', kind: 'date' },
        { name: 'documentLink', label: 'Document link', kind: 'text', wide: true },
        { name: 'notes', label: 'Notes', kind: 'textarea', wide: true },
      ],
      blank: {
        contractName: '',
        partyType: 'client',
        contractType: 'managed_services',
        counterpartyName: '',
        clientId: '',
        adminVendorId: '',
        startDate: '',
        endDate: '',
        termMonths: '',
        noticeDays: 60,
        autoRenew: true,
        status: 'active',
        monthlyValue: '',
        annualValue: '',
        seats: '',
        priceIncreasePercent: '',
        ownerEmployeeId: '',
        lastReviewed: '',
        documentLink: '',
        notes: '',
      },
      toForm: (row) => ({
        contractName: row.ContractName,
        partyType: row.PartyType,
        contractType: row.ContractType,
        counterpartyName: row.CounterpartyName,
        clientId: row.ClientId ?? '',
        adminVendorId: row.AdminVendorId ?? '',
        startDate: row.StartDate?.slice(0, 10) ?? '',
        endDate: row.EndDate?.slice(0, 10) ?? '',
        termMonths: row.TermMonths ?? '',
        noticeDays: row.NoticeDays,
        autoRenew: row.AutoRenew,
        status: row.Status,
        monthlyValue: row.MonthlyValue,
        annualValue: row.AnnualValue,
        seats: row.Seats ?? '',
        priceIncreasePercent: row.PriceIncreasePercent ?? '',
        ownerEmployeeId: row.OwnerEmployeeId ?? '',
        lastReviewed: row.LastReviewed?.slice(0, 10) ?? '',
        documentLink: row.DocumentLink ?? '',
        notes: row.Notes,
      }),
    }),
    [clients.data, vendors.data, employees.data],
  );

  const reload = () => {
    analytics.reload();
    onChanged();
  };

  const d = analytics.data;
  const hasContracts = (d?.summary.total ?? 0) > 0;

  return (
    <div className="admin-stack">
      {analytics.error && <ErrorBanner message={analytics.error} />}
      {!d && analytics.loading && <Loading label="Loading contracts" />}

      {d && hasContracts && (
        <>
          <div className="grid grid-4">
            <Stat
              label="Client MRR"
              value={formatCompactCurrency(d.summary.clientMrr)}
              foot={`${formatCompactCurrency(d.summary.clientArr)} annualised`}
            />
            <Stat
              label="Supplier cost"
              value={formatCompactCurrency(d.summary.vendorAnnualCost)}
              foot="annual, non-client contracts"
            />
            <Stat
              label="Value at risk"
              value={formatCompactCurrency(d.summary.valueAtRisk90)}
              foot={`${d.summary.expiring90} expiring within 90 days`}
              flagged={d.summary.expiring90 > 0}
            />
            <Stat
              label="Notice missed"
              value={d.summary.noticeMissed}
              flagged={d.summary.noticeMissed > 0}
              /* The expensive case, spelled out: the notice date is gone and the
                 term is still running, so the outcome — another term, or a lapse
                 — is no longer a choice. Counts both ways round; see the note in
                 adminAnalytics.ts. */
              foot="past the notice date, term still running"
            />
          </div>

          <div className="admin-split">
            <ChartCard
              title="Renewal pipeline"
              subtitle="Every contract by how far its end date is away, and what that band is worth."
              footer={
                d.summary.autoRenewSharePercent === null
                  ? undefined
                  : `${pct(d.summary.autoRenewSharePercent, 0)} of the book renews automatically.`
              }
              table={{
                columns: [
                  { key: 'label', label: 'Window' },
                  { key: 'count', label: 'Contracts', align: 'right' },
                  {
                    key: 'annualValue',
                    label: 'Annual value',
                    align: 'right',
                    render: (row: ContractsData['buckets'][number]) =>
                      formatCompactCurrency(row.annualValue),
                  },
                ],
                rows: d.buckets,
                rowKey: (row) => row.key,
              }}
            >
              <RankedBars
                rows={d.buckets.map((b) => ({
                  label: b.label,
                  value: b.annualValue,
                  meta: `${b.count} ${b.count === 1 ? 'contract' : 'contracts'}`,
                  /* Expired is the one band that is not a magnitude on the same
                     scale as the rest — it is a failure — so it takes the status
                     colour while the forward bands stay one hue. */
                  color: b.key === 'expired' ? 'var(--status-critical)' : undefined,
                }))}
                format={(v) => formatCompactCurrency(v)}
                labelWidth={124}
              />
            </ChartCard>

            <ChartCard
              title="Expiries by month"
              subtitle="The next eighteen months of end dates, by annual value."
              table={{
                columns: [
                  {
                    key: 'period',
                    label: 'Month',
                    render: (row: ContractsData['timeline'][number]) => periodLabel(row.period),
                  },
                  { key: 'count', label: 'Contracts', align: 'right' },
                  {
                    key: 'annualValue',
                    label: 'Annual value',
                    align: 'right',
                    render: (row: ContractsData['timeline'][number]) =>
                      formatCompactCurrency(row.annualValue),
                  },
                ],
                rows: d.timeline,
                rowKey: (row) => row.period,
              }}
            >
              <BarChart
                data={d.timeline}
                xKey="period"
                series={[{ key: 'annualValue', label: 'Annual value', color: 'var(--series-1)' }]}
                xFormat={periodLabel}
                yFormat={(v) => formatCompactCurrency(v)}
                height={240}
                emptyMessage="No contracts end in the next eighteen months."
              />
            </ChartCard>
          </div>

          <Card
            title="Coming up"
            subtitle="From a month overdue out to six months ahead — wider than the pipeline above, because a contract that ended three weeks ago is still somebody's problem."
          >
            <DataTable
              columns={UPCOMING_COLUMNS}
              rows={d.upcoming}
              rowKey={(row) => String(row.contractId)}
              empty="Nothing ends in the next six months."
            />
          </Card>

          <ChartCard
            title="By contract type"
            subtitle="Where the book's value sits."
            table={{
              columns: [
                {
                  key: 'contractType',
                  label: 'Type',
                  render: (row: ContractsData['byType'][number]) => snakeLabel(row.contractType),
                },
                { key: 'count', label: 'Contracts', align: 'right' },
                {
                  key: 'annualValue',
                  label: 'Annual value',
                  align: 'right',
                  render: (row: ContractsData['byType'][number]) =>
                    formatCompactCurrency(row.annualValue),
                },
              ],
              rows: d.byType,
              rowKey: (row) => row.contractType,
            }}
          >
            <RankedBars
              rows={d.byType.map((t) => ({
                label: snakeLabel(t.contractType),
                value: t.annualValue,
                meta: `${t.count} ${t.count === 1 ? 'contract' : 'contracts'}`,
              }))}
              format={(v) => formatCompactCurrency(v)}
            />
          </ChartCard>
        </>
      )}

      <RegisterPanel spec={spec} canWrite={canWrite} onChanged={reload} />
    </div>
  );
}

/**
 * The working list.
 *
 * Notice deadline is its own column rather than folded into the end date,
 * because they are different dates with different consequences and the earlier
 * one is the one that binds.
 */
const UPCOMING_COLUMNS: Column<ContractsData['upcoming'][number]>[] = [
  {
    key: 'contractName',
    label: 'Contract',
    render: (row) => (
      <div>
        <div className="admin-cell-title">{row.contractName}</div>
        <div className="xs muted">
          {row.clientName ?? row.counterpartyName} · {snakeLabel(row.contractType)}
        </div>
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => <StatusChip band={contractBand(row.status)}>{snakeLabel(row.status)}</StatusChip>,
  },
  {
    key: 'endDate',
    label: 'Ends',
    render: (row) => <DueCell date={row.endDate} days={row.daysToEnd} />,
  },
  {
    key: 'noticeDeadline',
    label: 'Notice by',
    render: (row) => (
      <div className="admin-due">
        <span>{formatIsoShort(row.noticeDeadline)}</span>
        {/* The notice window carries its own urgency, which is often worse than
            the end date's — that is the point of showing both. */}
        <StatusChip band={urgency(row.daysToNotice)}>{relativeDays(row.daysToNotice)}</StatusChip>
      </div>
    ),
  },
  {
    key: 'autoRenew',
    label: 'On expiry',
    render: (row) => (
      <span className={row.autoRenew ? undefined : 'admin-warn-text'}>
        {row.autoRenew ? 'Auto-renews' : 'Lapses'}
      </span>
    ),
  },
  {
    key: 'annualValue',
    label: 'Annual',
    align: 'right',
    render: (row) => formatCompactCurrency(row.annualValue),
  },
  {
    key: 'ownerName',
    label: 'Owner',
    render: (row) => row.ownerName ?? <span className="sub">—</span>,
  },
];
